import type { HandleClientError } from "@sveltejs/kit/hooks";
import * as Sentry from "@sentry/sveltekit";
import { dev } from "$app/env";
import { navigating, page, updated } from "$app/state";
import { isErrorWithStatus } from "#lib/errors.js";

const STALE_CHUNK_KEY = "stale-chunk-reload";

const STALE_CHUNK_REPORTED_KEY = "stale-chunk-reported";

let reloadedInPriorLoad = (() => {
	try {
		return !!sessionStorage.getItem(STALE_CHUNK_KEY);
	} catch {
		return false;
	}
})();

export const STALE_CHUNK_ERROR_OPERANDS = [
	"Failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"Importing a module script failed",
	"Unable to preload CSS",
	"reading 'universal'",
	"reading 'component'",
	"evaluating '",
	".universal'",
	".component'",
	'can\'t access property "universal"',
	'can\'t access property "component"'
] as const;

const [
	dynamicImportFetch,
	dynamicImportLoad,
	moduleScriptFailed,
	preloadCss,
	v8Universal,
	v8Component,
	jscEvaluating,
	jscUniversal,
	jscComponent,
	spiderMonkeyUniversal,
	spiderMonkeyComponent
] = STALE_CHUNK_ERROR_OPERANDS;

export function isStaleChunkError(error: unknown): boolean {
	const msg = error instanceof Error ? error.message : typeof error === "string" ? error : "";

	return (
		msg.includes(dynamicImportFetch) ||
		msg.includes(dynamicImportLoad) ||
		msg.includes(moduleScriptFailed) ||
		msg.includes(preloadCss) ||
		msg.includes(v8Universal) ||
		msg.includes(v8Component) ||
		(msg.includes(jscEvaluating) &&
			(msg.includes(jscUniversal) || msg.includes(jscComponent))) ||
		msg.includes(spiderMonkeyUniversal) ||
		msg.includes(spiderMonkeyComponent)
	);
}

export function isBrowserExtensionError(error: unknown): boolean {
	const msg = error instanceof Error ? error.message : typeof error === "string" ? error : "";
	return msg.startsWith("Invalid call to runtime.sendMessage()");
}

const NETWORK_FETCH_MESSAGES = [
	"Failed to fetch",
	"Load failed",
	"NetworkError when attempting to fetch resource"
] as const;

const NETWORK_FETCH_FAILURE_FINGERPRINT = "network-fetch-failure";
const NETWORK_FETCH_FAILURE_MESSAGE = "Network fetch failure";
const preloadFetchFailures = new WeakSet<object>();

type NamedError = { name: string; message: string };

function isTypeError(error: unknown): error is NamedError {
	if (error instanceof TypeError) return true;
	if (typeof error !== "object" || error === null) return false;
	const candidate = error as { name?: unknown; message?: unknown };
	return candidate.name === "TypeError" && typeof candidate.message === "string";
}

export function normalizeNetworkFetchMessage(error: unknown): string | null {
	if (!isTypeError(error)) return null;
	const raw = error.message;
	const stripped = raw
		.trim()
		.replace(/\s*\([^()]*\)$/, "")
		.replace(/\.$/, "")
		.trim();
	return NETWORK_FETCH_MESSAGES.some((message) => message === stripped)
		? NETWORK_FETCH_FAILURE_MESSAGE
		: null;
}

export type SentryEvent = {
	fingerprint?: string[];
	tags?: Record<string, unknown>;
	contexts?: Record<string, unknown>;
	exception?: { values?: { value?: string }[] };
};

export function shapeNetworkFetchEvent(event: SentryEvent, error: unknown): boolean {
	const normalized = normalizeNetworkFetchMessage(error);
	if (!normalized) return false;
	event.fingerprint = [NETWORK_FETCH_FAILURE_FINGERPRINT];
	const primary = event.exception?.values?.[0];
	if (primary?.value) {
		event.tags = { ...event.tags, originalMessage: primary.value };
		primary.value = normalized;
	}
	const diagnostics = collectNetworkFetchDiagnostics();

	event.tags = { ...event.tags, ...toTags(diagnostics) };
	event.contexts = { ...event.contexts, connectivity: diagnostics };
	return true;
}

function toTags(diagnostics: NetworkFetchDiagnostics): Record<string, string> {
	return Object.fromEntries(
		Object.entries(diagnostics).map(([key, value]) => [
			key,
			value === null ? "unknown" : String(value)
		])
	);
}

type NetworkInformation = { effectiveType?: string };

export type NetworkFetchDiagnostics = {
	onLine: boolean | null;
	effectiveType: string | null;
	visibilityState: string | null;
	buildIsCurrent: boolean | null;
	navigationType: string | null;
};

function readOrNull<T>(get: () => T): NonNullable<T> | null {
	try {
		return get() ?? null;
	} catch {
		return null;
	}
}

export function collectNetworkFetchDiagnostics(): NetworkFetchDiagnostics {
	return {
		onLine: readOrNull(() => navigator.onLine),
		effectiveType: readOrNull(
			() =>
				(navigator as Navigator & { connection?: NetworkInformation }).connection
					?.effectiveType
		),
		visibilityState: readOrNull(() => document.visibilityState),
		buildIsCurrent: readOrNull(() => !updated.current),
		navigationType: readOrNull(() => navigating.type ?? "none")
	};
}

const PAGE_LEAVE_WINDOW_MS = 3_000;
let pageLeftAt: number | null = null;

type NavigateEventFields = {
	destination: { sameDocument: boolean };
	downloadRequest: string | null;
};

export function recordPageLeave(event: NavigateEventFields): void {
	if (event.destination.sameDocument || event.downloadRequest !== null) return;
	pageLeftAt = Date.now();
}

export function clearPageLeave(): void {
	pageLeftAt = null;
}

export function isFetchCancelledByPageLeave(error: unknown): boolean {
	if (pageLeftAt === null || Date.now() - pageLeftAt > PAGE_LEAVE_WINDOW_MS) return false;

	const cause =
		typeof error === "object" && error !== null
			? (error as { cause?: unknown }).cause
			: undefined;

	return (
		normalizeNetworkFetchMessage(error) !== null || normalizeNetworkFetchMessage(cause) !== null
	);
}

// Stale-chunk workflow: a chunk load failure reloads the page once per tab session
// (STALE_CHUNK_KEY, shared with the pre-hydration listener in app.html). A failure after
// that reload is reported once (STALE_CHUNK_REPORTED_KEY). A load that passes the settle
// window with no stale chunk clears both keys so the next deploy gets its own reload.
const STALE_CHUNK_GUARD_SETTLE_MS = 30_000;
let staleChunkSeenThisLoad = false;

function guardedReload(targetUrl?: URL): boolean {
	staleChunkSeenThisLoad = true;
	try {
		if (!sessionStorage.getItem(STALE_CHUNK_KEY)) {
			sessionStorage.setItem(STALE_CHUNK_KEY, "1");
			if (targetUrl) window.location.assign(targetUrl.href);
			else window.location.reload();
			return true;
		}
	} catch {
		return false;
	}
	return false;
}

function handleStaleChunk(error: unknown, targetUrl?: URL): void {
	if (guardedReload(targetUrl)) return;

	if (!reloadedInPriorLoad) return;
	try {
		if (sessionStorage.getItem(STALE_CHUNK_REPORTED_KEY)) return;
		sessionStorage.setItem(STALE_CHUNK_REPORTED_KEY, "1");
	} catch {
		return;
	}
	const message = error instanceof Error ? error.message : String(error);
	Sentry.captureException(new Error(`Persistent chunk load failure after reload: ${message}`), {
		tags: { staleChunkReloadFailed: true }
	});
}

const VERSION_CHECK_COOLDOWN_MS = 10_000;
let versionCheckInFlight: Promise<boolean> | null = null;
let versionCheckedAt = 0;

async function checkForUpdatedBuild(force = false): Promise<boolean> {
	if (updated.current) return true;
	if (versionCheckInFlight) return versionCheckInFlight;
	if (!force && Date.now() - versionCheckedAt < VERSION_CHECK_COOLDOWN_MS) return false;
	versionCheckInFlight = updated
		.check()
		.catch(() => false)
		.finally(() => {
			versionCheckInFlight = null;
			versionCheckedAt = Date.now();
		});
	return versionCheckInFlight;
}

function handleUnclassifiedError(error: unknown): void {
	void checkForUpdatedBuild().then((stale) => {
		if (stale) handleStaleChunk(error);
	});
}

if (typeof window !== "undefined") {
	setTimeout(() => {
		if (staleChunkSeenThisLoad) return;
		reloadedInPriorLoad = false;
		try {
			sessionStorage.removeItem(STALE_CHUNK_KEY);
			sessionStorage.removeItem(STALE_CHUNK_REPORTED_KEY);
		} catch {
			return;
		}
	}, STALE_CHUNK_GUARD_SETTLE_MS);

	if ("navigation" in window) window.navigation.addEventListener("navigate", recordPageLeave);
	window.addEventListener("pageshow", (event) => {
		if (event.persisted) clearPageLeave();
	});

	window.addEventListener("vite:preloadError", (event) => {
		if (guardedReload()) event.preventDefault();
	});

	window.addEventListener("unhandledrejection", (event) => {
		if (isStaleChunkError(event.reason)) handleStaleChunk(event.reason);
		else handleUnclassifiedError(event.reason);
	});
	window.addEventListener("error", (event) => {
		if (isStaleChunkError(event.error)) handleStaleChunk(event.error);
		else handleUnclassifiedError(event.error);
	});
}

if (!dev)
	Sentry.init({
		dsn: "https://ac55f89629c3b495c0c719c08c7e35f8@o4506626928672768.ingest.us.sentry.io/4506626929786880",
		tunnel: "/api/sentry",
		tracesSampleRate: dev ? 1.0 : 0.1,
		integrations: [
			Sentry.thirdPartyErrorFilterIntegration({
				filterKeys: ["appfergmarcom"],
				behaviour: "drop-error-if-exclusively-contains-third-party-frames"
			})
		],
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: dev ? 0 : 1.0,
		environment: dev ? "development" : "production",
		beforeSend(event, hint) {
			const error = hint.originalException;

			if (
				error instanceof Error &&
				error.message.includes("ResizeObserver loop limit exceeded")
			) {
				return null;
			}

			if (navigator.userAgent.includes("Lightpanda")) {
				return null;
			}

			if (error instanceof Error && error.message.includes("Java object is gone")) {
				return null;
			}

			if (isBrowserExtensionError(error)) {
				return null;
			}

			if (
				event.exception?.values?.some((v) =>
					v.stacktrace?.frames?.some((f) => f.filename?.includes("/~partytown/"))
				)
			) {
				return null;
			}

			if (isStaleChunkError(error) && !event.tags?.staleChunkReloadFailed) {
				return null;
			}

			if (isFetchCancelledByPageLeave(error)) {
				return null;
			}

			if (typeof error === "object" && error !== null && preloadFetchFailures.has(error)) {
				return null;
			}

			shapeNetworkFetchEvent(event, error);

			return event;
		}
	});

if (!dev) {
	const loadReplay = () => {
		if (Sentry.getClient()?.getIntegrationByName("Replay")) return;
		Sentry.addIntegration(
			Sentry.replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
				useCompression: false,
				maskAllInputs: false
			})
		);
	};

	if ("requestIdleCallback" in window) {
		requestIdleCallback(loadReplay);
	} else {
		setTimeout(loadReplay, 3_000);
	}
}

const myErrorHandler: HandleClientError = (input) => {
	console.error(input.error, input.event);

	const caught = isErrorWithStatus(input.error) ? input.error : undefined;
	const captured = input.kind === "unknown" || (caught !== undefined && caught.status >= 500);

	return {
		message:
			input.kind === "unknown"
				? "An unexpected error occurred"
				: (caught?.message ?? "An unexpected error occurred"),
		sentryEventId: captured ? Sentry.lastEventId() : undefined
	};
};

const sentryErrorHandler = Sentry.handleErrorWithSentry(myErrorHandler);

export const handleError: HandleClientError = async (input) => {
	if (isTypeError(input.error) && normalizeNetworkFetchMessage(input.error)) {
		const currentUrl = new URL(window.location.href);
		if (
			page.route.id !== null &&
			navigating.type === null &&
			(input.event.url.origin !== currentUrl.origin ||
				input.event.url.pathname !== currentUrl.pathname ||
				input.event.url.search !== currentUrl.search)
		) {
			preloadFetchFailures.add(input.error);
		} else {
			preloadFetchFailures.delete(input.error);
		}
	}

	if (isStaleChunkError(input.error)) {
		handleStaleChunk(input.error, input.event.url);
		return;
	}

	if (
		navigating.type === null &&
		input.event.url.href === window.location.href &&
		(await checkForUpdatedBuild(true))
	) {
		handleStaleChunk(input.error, input.event.url);
		return;
	}

	return sentryErrorHandler(input);
};
